// /disputes — the dispute operations queue (SM-291 / SM-295 / SM-320).
//
// A separate workspace on purpose. Disputes are NOT a variation of
// market resolution: the operator population is different (DISPUTE_REVIEW,
// not RULE_RESOLVE), the urgency model is different (an unreviewed
// dispute holds every holder's payout hostage), and the failure mode is
// different (a decision here re-settles a market that already looked
// resolved). Folding them into the market detail panel would bury a
// time-critical queue behind a catalog browser.
//
// ONE queue, newest first, with a status filter (SM-320). It used to be
// five tabs — New / Waiting / Ready / Overdue / Resolved — and each tab
// was its own server-side read, which made the page answer "what is in
// this bucket" but never "what has been filed". Three consequences drove
// the rewrite:
//
//   * New and Waiting were the same rows (both plain OPEN), so two tabs
//     competed to show one set while every row in them read "Open".
//   * PENDING_FUNDING and FAILED disputes belonged to no tab at all and
//     were therefore invisible in the UI, ledger questions included.
//   * Landing on one tab hid the rest, so the operator had to click
//     through five reads to see whether anything needed them.
//
// The filter offers exactly the values a row's badge can show, so the
// list and the filter can never disagree about what a row is; the
// derivation they share is disputeFilterOf(). Overdue is the one derived
// value in that vocabulary and stays selectable, because "past the review
// deadline" is the thing an operator hunts for.
//
// Overdue also stays visible FROM a filtered view: its whole-table count
// rides on every response, so narrowing to Resolved cannot hide an
// incident where holders are still locked out.
//
// URL state carries the filter, the search and the page so a triage view
// is shareable, matching the ops findings inbox. Bookmarked ?queue= links
// from the tab era still resolve to the rows they named.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { Input } from "@/components/ui/Input";
import { Pagination } from "@/components/ui/Pagination";
import { Skeleton } from "@/components/ui/Skeleton";
import { SparkAmount } from "@/components/ui/SparkAmount";
import { useMyPerms } from "@/hooks/useMyPerms";
import { cn } from "@/lib/cn";
import {
  DISPUTE_STATUS_FILTERS,
  disputeFilterHint,
  disputeFilterLabel,
  disputeEventLabel,
  disputeFilterTone,
  disputeMarketLabel,
  disputeStatusLabel,
  disputeTone,
  disputeUserLabel,
  formatDuration,
  untilMs,
  useDisputes,
  type DisputeStatusFilter,
} from "@/lib/dispute-queries";
import { useEventNames, useMarketNames } from "@/hooks/useCatalogNames";
import { useUserNames } from "@/hooks/useUserNames";
import { fmtRelative } from "@/lib/format";
import { Perm } from "@/lib/perms";
import { translatePageCopy } from "@/i18n";

const PAGE_SIZE = 25;

interface Search {
  /** Canonical CSV of DisputeStatusFilter values; absent = every status. */
  status?: string;
  q?: string;
  page?: number;
}

// What the retired tabs selected. Kept so a bookmarked or pasted
// ?queue=overdue lands on the overdue rows instead of silently widening
// to everything — the server honours the param too, but the UI has to
// agree about what is selected or the filter chip would read "All".
const LEGACY_QUEUE_STATUSES: Record<string, DisputeStatusFilter[]> = {
  new: ["OPEN"],
  waiting: ["OPEN"],
  ready: ["READY_FOR_REVIEW"],
  overdue: ["OVERDUE"],
  // VOID is listed alongside ACCEPTED because the tab meant "everything
  // decided": since SM-325 split voids out of ACCEPTED, dropping it here
  // would make a bookmarked ?queue=resolved hide every voided market.
  resolved: ["ACCEPTED", "VOID", "REJECTED"],
};

/**
 * Parse + canonicalise the filter: unknown values dropped, duplicates
 * collapsed, always in menu order. Canonical form matters beyond
 * tidiness — the CSV is part of the react-query key, so "OPEN,ACCEPTED"
 * and "ACCEPTED,OPEN" must not be two cache entries for one result set.
 */
function parseStatuses(v: unknown): DisputeStatusFilter[] {
  if (typeof v !== "string" || !v) return [];
  const wanted = new Set(v.split(",").map((s) => s.trim().toUpperCase()));
  return DISPUTE_STATUS_FILTERS.filter((s) => wanted.has(s));
}

const toCsv = (s: DisputeStatusFilter[]): string | undefined =>
  s.length > 0 ? s.join(",") : undefined;

export const Route = createFileRoute("/_app/disputes/")({
  validateSearch: (s: Record<string, unknown>): Search => {
    const selected = parseStatuses(s.status);
    const legacy =
      typeof s.queue === "string" ? LEGACY_QUEUE_STATUSES[s.queue.toLowerCase()] : undefined;
    return {
      // No status and no legacy queue means All — the default view is
      // every dispute, which is the point of the unified queue.
      status: toCsv(selected.length > 0 ? selected : (legacy ?? [])),
      q: typeof s.q === "string" && s.q ? s.q : undefined,
      page: Number.isFinite(Number(s.page)) && Number(s.page) > 0 ? Number(s.page) : undefined,
    };
  },
  component: DisputesPage,
});

function DisputesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const perms = useMyPerms();

  const selected = parseStatuses(search.status);
  const page = search.page ?? 0;
  const [draft, setDraft] = useState(search.q ?? "");
  // The last search term this page put in the URL. Lets the sync below
  // tell "the URL changed under us" (Back, Reset, a pasted link) from
  // "the URL changed because we just pushed it".
  const pushedQ = useRef(search.q);

  const q = useDisputes({
    status: search.status,
    q: search.q,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    // Newest first, always. A submission time is the one ordering that
    // means the same thing for every status in the list — the deadline
    // sorts the tabs used to apply are undefined for half of them.
    sort_by: "created_at",
    sort_dir: "desc",
  });

  const items = q.data?.items ?? [];

  // Client-side second chance at the names the server already tries to
  // fill. Only ids that arrived UNNAMED are looked up, so a healthy
  // response costs nothing; a degraded one still gets an operator a
  // readable queue instead of a wall of ULIDs (SM-349 AC 7).
  //
  // Catalog lookups are inert without CATALOG_VIEW_ALL: that viewer's
  // ids have already been swapped for opaque digests upstream, so every
  // request would 404 — and not being able to enumerate the catalog is
  // the point of the redaction.
  const resolveCatalog = perms.loaded && perms.has(Perm.CatalogViewAll);
  const unnamedMarketIDs = useMemo(
    () => items.filter((d) => !d.market_question).map((d) => d.market_id),
    [items],
  );
  const unnamedEventIDs = useMemo(
    () => items.filter((d) => !d.event_title && d.event_id).map((d) => d.event_id!),
    [items],
  );
  const unnamedUserIDs = useMemo(
    () =>
      items
        .filter((d) => !d.user_nickname && !d.user_email && d.user_id)
        .map((d) => d.user_id!),
    [items],
  );
  const marketNames = useMarketNames(unnamedMarketIDs, undefined, resolveCatalog);
  const eventNames = useEventNames(unnamedEventIDs, resolveCatalog);
  const userNames = useUserNames(unnamedUserIDs, perms.has(Perm.UserView));

  const counts = q.data?.counts ?? {};
  const total = q.data?.page?.total ?? 0;
  const overdueCount = counts.overdue ?? 0;
  const filtered = selected.length > 0 || Boolean(search.q);

  // Any change to what is being selected resets to page 1: the row that
  // was on page 3 of the old result set is somewhere else in the new one,
  // and an out-of-range offset renders an empty page over a non-empty
  // result.
  function setStatuses(next: DisputeStatusFilter[]) {
    void navigate({ search: (s) => ({ ...s, status: toCsv(next), page: undefined }) });
  }

  // Search applies as you type (debounced) rather than on Enter. Typed
  // text that has not been submitted is a filter the operator believes is
  // applied while the list still shows everything, and the box gives no
  // hint that a keystroke is owed. Same page reset as the status filter —
  // an offset from the previous result set means nothing in the new one.
  useEffect(() => {
    const next = draft.trim() || undefined;
    if (next === search.q) return;
    const t = window.setTimeout(() => {
      pushedQ.current = next;
      void navigate({ search: (s) => ({ ...s, q: next, page: undefined }) });
    }, 350);
    return () => window.clearTimeout(t);
  }, [draft, search.q, navigate]);

  // …and adopt a search term that arrived from anywhere else. Without
  // this, pressing Back would leave the stale keystrokes in the box and
  // the debounce would immediately push them again — Back would look
  // broken.
  useEffect(() => {
    if (search.q === pushedQ.current) return;
    pushedQ.current = search.q;
    setDraft(search.q ?? "");
  }, [search.q]);

  if (!perms.has(Perm.DisputeView)) {
    return (
      <EmptyState
        title="No access"
        description="You need the DISPUTE_VIEW permission to see the dispute queue."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl tracking-tight">Disputes</h1>
          <p className="text-sm text-muted">
            Holder-funded challenges to a provisional market result. While a dispute is
            open the market stays locked — no holder can redeem until it is decided.
          </p>
        </div>
        <div className="flex gap-2">
          {perms.has(Perm.DisputePolicy) && (
            <Link to="/disputes/policy">
              <Button variant="secondary">Policy</Button>
            </Link>
          )}
          {perms.has(Perm.AlertView) && (
            <Link to="/disputes/alerts">
              <Button variant="secondary">Alerts</Button>
            </Link>
          )}
        </div>
      </div>

      {/* Overdue is an incident, not a backlog item: every position on
          the affected markets is still locked. The count is whole-table,
          so this shows even while the list is filtered to something
          else — that is what replaces the tab badge. */}
      {overdueCount > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-danger/40 bg-danger/10 px-4 py-3 text-sm">
          <span className="font-medium text-danger tabular-nums">
            {overdueCount} overdue
          </span>
          <span className="text-muted">
            past the review deadline — those markets are still locked and no holder can
            redeem until a decision is made.
          </span>
          {!(selected.length === 1 && selected[0] === "OVERDUE") && (
            <Button
              variant="secondary"
              size="sm"
              className="ml-auto"
              onClick={() => setStatuses(["OVERDUE"])}
            >
              Show overdue
            </Button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <StatusFilterMenu selected={selected} onChange={setStatuses} />
        <form
          className="min-w-[220px] flex-1"
          // Kept a <form> so Enter is a no-op instead of a page reload;
          // the debounce above has already applied whatever is typed.
          onSubmit={(e) => e.preventDefault()}
        >
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Search dispute / market / user id"
            aria-label="Search disputes"
          />
        </form>
        {filtered && (
          <Button
            variant="ghost"
            onClick={() => {
              setDraft("");
              void navigate({ search: () => ({}) });
            }}
          >
            Reset
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="gap-3 border-b border-border">
          {/* The heading names the applied filter, so the row count below
              it is never ambiguous about what it counted. */}
          <CardTitle>
            {selected.length === 0
              ? "All disputes"
              : selected.map(disputeFilterLabel).join(" · ")}
          </CardTitle>
          <span className="text-2xs text-muted tabular-nums">{total}</span>
          {search.q && (
            <span className="text-2xs text-muted">matching “{search.q}”</span>
          )}
        </CardHeader>
        <CardBody className="p-0">
          {q.isLoading && (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-14 rounded-lg" />
              ))}
            </div>
          )}
          {q.isError && <ErrorPanel error={q.error} reset={() => void q.refetch()} />}
          {!q.isLoading && !q.isError && items.length === 0 && (
            <EmptyState
              title="Nothing here"
              description={
                filtered
                  ? "No disputes match this filter. Clear it to see every dispute."
                  : "No disputes have been filed."
              }
            />
          )}
          {items.length > 0 && (
            <ul className="divide-y divide-border">
              {items.map((d) => {
                const windowLeft = untilMs(d.window_ends_at_ms);
                const reviewLeft = untilMs(d.review_due_at_ms);
                return (
                  <li key={d.dispute_id}>
                    <Link
                      to="/disputes/$id"
                      params={{ id: d.dispute_id }}
                      className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-3 transition-colors hover:bg-surface-2"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          {/* Every status is in this list now, so the
                              badge is load-bearing rather than
                              decoration: it is the only thing saying
                              which of them a row is. */}
                          <Badge tone={disputeTone(d)}>{disputeStatusLabel(d)}</Badge>
                          {/* The MARKET leads, because that is what the
                              dispute targets and what the decision will
                              change. The event follows as context — on a
                              multi-market event the market name alone
                              ("Brazil") does not say which contest it
                              belongs to, and the event alone does not
                              say which leg is contested. */}
                          <span className="truncate text-sm font-medium text-fg">
                            {disputeMarketLabel(d, marketNames)}
                          </span>
                          {disputeEventLabel(d, eventNames) && (
                            <span className="truncate text-xs text-muted">
                              in {disputeEventLabel(d, eventNames)}
                            </span>
                          )}
                        </div>
                        <p className="mt-0.5 truncate text-2xs text-muted">
                          {disputeUserLabel(d, userNames)} ·{" "}
                          {fmtRelative(new Date(d.created_at_ms).toISOString())}
                        </p>
                      </div>

                      <SparkAmount value={d.stake_amount} className="text-sm tabular-nums" />

                      {/* One deadline column, whichever deadline is the
                          live one: the window while it runs, then the
                          review clock. Showing both would make the
                          urgent number compete with a spent one. */}
                      <div className="w-32 shrink-0 text-right text-2xs">
                        {windowLeft ? (
                          <span className="text-muted">
                            window {formatDuration(windowLeft)}
                          </span>
                        ) : d.overdue ? (
                          <span className="font-medium text-danger">review overdue</span>
                        ) : reviewLeft ? (
                          <span className="text-warning">
                            review in {formatDuration(reviewLeft)}
                          </span>
                        ) : d.decided_at_ms ? (
                          <span className="text-muted">
                            {fmtRelative(new Date(d.decided_at_ms).toISOString())}
                          </span>
                        ) : null}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>

      <Pagination
        page={{
          limit: PAGE_SIZE,
          offset: page * PAGE_SIZE,
          total,
          has_more: (page + 1) * PAGE_SIZE < total,
        }}
        currentPage={page}
        onPageChange={(next) =>
          void navigate({ search: (s) => ({ ...s, page: next || undefined }) })
        }
        itemLabel="disputes"
        className="justify-center"
      />
    </div>
  );
}

/**
 * The status filter: a button that opens a checkbox list, so several
 * statuses can be selected at once and "All" is one click back.
 *
 * Native checkboxes rather than a custom listbox (the shared Select is
 * single-select) — they carry keyboard operation, focus order and the
 * checked state to assistive tech for free, which a div-with-role
 * reimplementation of a 7-item filter would only get wrong.
 */
function StatusFilterMenu({
  selected,
  onChange,
}: {
  selected: DisputeStatusFilter[];
  onChange: (next: DisputeStatusFilter[]) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Dismissal: outside pointer-down and Escape. Escape restores focus to
  // the trigger so a keyboard user is not dropped at the top of the page.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = (v: DisputeStatusFilter) =>
    onChange(
      selected.includes(v)
        ? selected.filter((x) => x !== v)
        // Re-derive from the canonical order rather than appending, so
        // the URL and the query key do not depend on click order.
        : DISPUTE_STATUS_FILTERS.filter((x) => x === v || selected.includes(x)),
    );

  const summary =
    selected.length === 0
      ? t("common.filterSummary.allStatuses")
      : selected.length === 1
        ? translatePageCopy(disputeFilterLabel(selected[0]))
        : t("common.filterSummary.statusCount", { count: selected.length });

  return (
    <div className="relative" ref={rootRef}>
      <Button
        ref={triggerRef}
        variant="secondary"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        // Explicit name: the visible text is split across spans plus an
        // icon, which leaves the computed name unreliable.
        aria-label={t("common.filterSummary.byStatus", { summary })}
        className={cn(selected.length > 0 && "border-neon/40 text-t-1")}
      >
        <span className="text-2xs uppercase tracking-wide text-muted">Status</span>
        <span>{summary}</span>
        <svg
          aria-hidden="true"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          className={cn("transition-transform", open && "rotate-180")}
        >
          <path
            d="M3 4.5l3 3 3-3"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </Button>

      {open && (
        <div
          role="group"
          aria-label="Filter disputes by status"
          className="absolute left-0 z-30 mt-1 w-80 rounded-lg border border-border bg-surface p-1 shadow-card"
        >
          <button
            type="button"
            onClick={() => onChange([])}
            className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm text-fg hover:bg-surface-2"
          >
            All statuses
            {selected.length === 0 && <span aria-hidden="true">✓</span>}
          </button>
          <div className="my-1 border-t border-border" />
          {DISPUTE_STATUS_FILTERS.map((v) => (
            <label
              key={v}
              className="flex cursor-pointer items-start gap-3 rounded-md px-3 py-2 hover:bg-surface-2"
            >
              <input
                type="checkbox"
                className="mt-0.5 accent-neon"
                checked={selected.includes(v)}
                onChange={() => toggle(v)}
                // The label wraps a Badge and a hint, so name the control
                // outright rather than relying on the wrapper's text.
                aria-label={translatePageCopy(disputeFilterLabel(v))}
              />
              <span className="min-w-0">
                <Badge tone={disputeFilterTone(v)}>{disputeFilterLabel(v)}</Badge>
                <span className="mt-1 block text-2xs text-muted">
                  {disputeFilterHint(v)}
                </span>
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
